import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { Card, CardBody, CardHeader, CardFooter } from '../../components/ui/Card';
import { CountrySelect } from '../../components/ui/CountrySelect';
import { CurrencyMultiSelect } from '../../components/ui/CurrencySelect';

const INDUSTRY_KEYS = [
  'saas', 'manufacturing', 'trading', 'consulting', 'media', 'construction', 'healthcare', 'other',
];

export function OnboardingPage() {
  const { t, i18n } = useTranslation();
  const { data: user, isLoading } = useCurrentUser();
  const navigate = useNavigate();

  type Template = 'tokyo-saas-global' | 'us-saas-opening-jp' | 'jp-manufacturing' | 'jp-eu-services' | 'apac-holding';

  const [name, setName] = useState('');
  const [country, setCountry] = useState('JP');
  const [industry, setIndustry] = useState('saas');
  const [currencies, setCurrencies] = useState<string[]>(['JPY']);
  const [jpCorp, setJpCorp] = useState('');
  const [jpInvoice, setJpInvoice] = useState('');
  const [url, setUrl] = useState('');
  const [template, setTemplate] = useState<Template>('tokyo-saas-global');
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!isLoading && !user) {
      window.location.assign('/');
    }
  }, [isLoading, user]);

  useEffect(() => {
    api.get('/geo/hint').then((r: any) => {
      if (r.data?.countryCode) setCountry(r.data.countryCode);
      if (r.data?.suggestedCurrency) setCurrencies([r.data.suggestedCurrency]);
    }).catch(() => {});
  }, []);

  // Helpful: when locale region is known, use it to label country names.
  const countryDisplay = (code: string) => {
    try {
      return new Intl.DisplayNames([i18n.language], { type: 'region' }).of(code) ?? code;
    } catch {
      return code;
    }
  };

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      const r = await api.post<{ workspaceId: string }>('/companies', {
        name,
        countryCode: country,
        industry,
        transactedCurrencies: currencies,
        jpCorpNumber: jpCorp || undefined,
        jpInvoiceRegistration: jpInvoice || undefined,
        url: url || undefined,
        template,
      });
      navigate(`/app/workspace/${r.data.workspaceId}/inbox`);
    } catch (e: any) {
      setErr(e?.response?.data?.message ?? t('onboarding.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  }

  if (isLoading || !user) return null;

  return (
    <div className="min-h-screen bg-ink-50 dark:bg-ink-950 flex items-center justify-center p-6">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <div className="font-display text-2xl font-semibold">{t('onboarding.title')}</div>
          <p className="text-sm text-ink-500 mt-1">{t('onboarding.subtitle')}</p>
        </CardHeader>
        <form onSubmit={submit}>
          <CardBody className="space-y-5">
            <Field label={t('onboarding.companyName')}>
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full h-10 px-3 rounded-lg border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900"
              />
            </Field>
            <div className="grid md:grid-cols-2 gap-5">
              <Field label={t('onboarding.countryHq')}>
                <CountrySelect value={country} onChange={setCountry} />
              </Field>
              <Field label={t('onboarding.industry')}>
                <select
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  className="w-full h-10 px-3 rounded-lg border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900"
                >
                  {INDUSTRY_KEYS.map((i) => (
                    <option key={i} value={i}>{t(`onboarding.industryOptions.${i}`)}</option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label={t('onboarding.currenciesYouTransactIn')}>
              <CurrencyMultiSelect value={currencies} onChange={setCurrencies} />
            </Field>
            <Field label={t('onboarding.template.label')} hint={t('onboarding.template.hint')}>
              <div className="grid sm:grid-cols-2 gap-2">
                {(['tokyo-saas-global','us-saas-opening-jp','jp-manufacturing','jp-eu-services','apac-holding'] as const).map((k) => {
                  const active = template === k;
                  return (
                    <button
                      type="button"
                      key={k}
                      onClick={() => setTemplate(k)}
                      className={`text-left p-3 rounded-lg border transition-colors ${
                        active
                          ? 'border-accent-600 bg-accent-50 dark:bg-accent-900/30'
                          : 'border-ink-200 dark:border-ink-700 hover:border-ink-400'
                      }`}
                    >
                      <div className="text-sm font-medium">{t(`onboarding.template.items.${k}.title`)}</div>
                      <div className="text-xs text-ink-500 mt-0.5">{t(`onboarding.template.items.${k}.body`)}</div>
                    </button>
                  );
                })}
              </div>
            </Field>
            {country === 'JP' && (
              <div className="grid md:grid-cols-2 gap-5">
                <Field label={t('onboarding.jpCorpOptional')} hint={t('onboarding.jpCorpHint')}>
                  <input
                    value={jpCorp}
                    onChange={(e) => setJpCorp(e.target.value)}
                    placeholder="1234567890123"
                    className="w-full h-10 px-3 rounded-lg border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900 num"
                  />
                </Field>
                <Field label={t('onboarding.jpInvoiceOptional')} hint={t('onboarding.jpInvoiceHint')}>
                  <input
                    value={jpInvoice}
                    onChange={(e) => setJpInvoice(e.target.value)}
                    placeholder="T1234567890123"
                    className="w-full h-10 px-3 rounded-lg border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900 num"
                  />
                </Field>
              </div>
            )}
            <Field label={t('onboarding.websiteOptional')}>
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder={t('onboarding.websitePlaceholder')}
                className="w-full h-10 px-3 rounded-lg border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900"
              />
            </Field>
            {err && <div className="text-sm text-warn-600">{err}</div>}
          </CardBody>
          <CardFooter>
            <button
              type="submit"
              disabled={submitting || !name}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg font-medium h-11 px-6 bg-accent-600 text-white hover:bg-accent-700 disabled:opacity-50"
            >
              {submitting ? t('onboarding.submitting') : t('onboarding.submit')}
            </button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs uppercase tracking-wider text-ink-500 mb-1">
        {label}
        {hint && <span className="text-ink-400 normal-case ml-2">{hint}</span>}
      </div>
      {children}
    </label>
  );
}
